import isString from 'lodash/isString'
import { action, computed, makeObservable, observable } from 'mobx'
import { User as OidcUser } from 'oidc-client-ts'

import { AuthFields, formatUserPrivileges } from 'Shared/utils/auth'
import { storage } from 'Shared/utils/storage'

import { STORAGE_USER_TOKEN } from 'Core/constants'

export default class BasicAuthStore<UserRole extends {}, Permission extends {}> {
	@observable authData: OidcUser | null = null
	@observable impersonatorMode?: string

	constructor() {
		makeObservable(this)
	}

	@action.bound
	setUserData(authData: OidcUser | null) {
		this.authData = authData
		// with automaticSilentRenew = true from IODC config, barer token should be refreshed aswell (based on localstorage)
		if (authData) {
			storage.set(STORAGE_USER_TOKEN, authData.access_token)
		}
	}

	@computed
	get isImpersonatedMode(): boolean {
		return !!this.authData?.profile[AuthFields.impersonatorId]
	}

	@computed
	get impersonatorViewPermission(): Permission | null {
		if (!this.authData) {
			return null
		}

		return this.authData.profile[AuthFields.impersonatorViewPermission] as Permission
	}

	@action
	getPermissionViewMode(permission: Permission): boolean {
		if (!this.authData) {
			return false
		}

		const privilegeData = this.authData.profile[AuthFields.privileges] as
			| Permission
			| Permission[]

		if (isString(privilegeData)) {
			return privilegeData === permission
		}

		const privileges = privilegeData as Permission[]

		return privileges.includes(permission)
	}

	@computed
	get currentUserId(): BasicString {
		return this.authData?.profile?.sub || null
	}

	@computed
	get currentUserName(): string | undefined {
		return this.authData?.profile[AuthFields.userName] as string | undefined
	}

	@computed
	get permissions() {
		return formatUserPrivileges(this.authData)
	}

	@computed
	get userRoles() {
		return this.authData?.profile[AuthFields.userRole] as UserRole[]
	}

	isPermissionsExist = (userPermissions: Permission[]) => {
		const permissions = formatUserPrivileges(this.authData)
		return userPermissions.some((p) => permissions.includes(p))
	}
}
